import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscTrash, VscRefresh } from 'react-icons/vsc';
import {
  getMeggerTests, createMeggerTest, updateMeggerTest, updateMeggerRow, addNewMeggerCables, deleteMeggerTest,
} from '../../api/reports';
import PrintButton from '../common/PrintButton';
import useSwitchboardPicker, { SwitchboardSelect } from './useSwitchboardPicker';
import './Reports.css';

const PRINT_ROLES  = ['admin', 'engineer', 'manager', 'foreman', 'team-leader'];
const DELETE_ROLES = ['admin', 'engineer', 'manager', 'foreman'];
const VOLTAGES     = ['250 V', '500 V', '1000 V'];
const ROW_FIELDS   = ['initial', 'corrected', 'remarks'];

const isoDate  = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const showDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const filled   = (t) => t.rows.filter(r => r.initial || r.corrected).length;

const headerDraft = (t) => ({
  date: isoDate(t.date), testVoltage: t.testVoltage || '', measuredBy: t.measuredBy || '', remarks: t.remarks || '',
});

// Enter jumps to the next reading field, like Tab (handy on a phone keyboard)
const focusNextOnEnter = (e) => {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  const inputs = [...e.currentTarget.closest('table').querySelectorAll('input')];
  const next = inputs[inputs.indexOf(e.currentTarget) + 1];
  next ? next.focus() : e.currentTarget.blur();
};

function MeggerLists({ project, role, onOpenTab }) {
  const picker = useSwitchboardPicker(project);
  const sb = picker.selected;
  const canEdit = role !== 'client';

  const [tests, setTests]       = useState([]);
  const [testId, setTestId]     = useState('');
  const [header, setHeader]     = useState(null);   // draft of the selected test's header fields
  const [rowStatus, setRowStatus] = useState({});   // rowId -> 'saving' | 'saved' | 'error'
  const [notice, setNotice]     = useState('');
  const savedRef = useRef({});                      // rowId -> last saved values, to skip unchanged blurs

  const test = tests.find(t => t._id === testId) || null;

  const remember = (t) => t.rows.forEach(r => { savedRef.current[r._id] = { ...r }; });

  useEffect(() => {
    setTests([]); setTestId(''); setNotice('');
    if (!sb) return;
    getMeggerTests(sb._id).then(r => {
      r.data.forEach(remember);
      setTests(r.data);
      setTestId(r.data[0]?._id || '');
    }).catch(() => {});
  }, [sb?._id]);

  useEffect(() => { setHeader(test ? headerDraft(test) : null); setNotice(''); }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  const replaceTest = (t) => setTests(prev => prev.map(x => (x._id === t._id ? t : x)));

  const handleNew = async () => {
    try {
      const r = await createMeggerTest({ switchboard: sb._id });
      remember(r.data);
      setTests(prev => [r.data, ...prev]);
      setTestId(r.data._id);
    } catch (err) {
      setNotice(err.response?.data?.msg || 'Could not create the test.');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete the megger test from ${showDate(test.date)}? The readings are lost.`)) return;
    try {
      await deleteMeggerTest(test._id);
      const rest = tests.filter(t => t._id !== test._id);
      setTests(rest);
      setTestId(rest[0]?._id || '');
    } catch (err) {
      setNotice(err.response?.data?.msg || 'Could not delete the test.');
    }
  };

  const handleAddNew = async () => {
    try {
      const r = await addNewMeggerCables(test._id);
      remember(r.data.test);
      replaceTest(r.data.test);
      setNotice(r.data.added ? `Added ${r.data.added} new cable${r.data.added > 1 ? 's' : ''}.` : 'No new cables on this switchboard.');
    } catch {
      setNotice('Could not check for new cables.');
    }
  };

  const saveHeader = async (field) => {
    if (!test || header[field] === headerDraft(test)[field]) return;
    if (field === 'date' && !header.date) { setHeader(headerDraft(test)); return; }
    try {
      replaceTest((await updateMeggerTest(test._id, { [field]: header[field] })).data);
    } catch {
      setNotice('Could not save — check your connection.');
    }
  };

  const setRowField = (rowId, field, value) => replaceTest({
    ...test, rows: test.rows.map(r => (r._id === rowId ? { ...r, [field]: value } : r)),
  });

  const saveRow = async (row) => {
    const saved = savedRef.current[row._id] || {};
    const changes = Object.fromEntries(ROW_FIELDS.filter(f => (row[f] || '') !== (saved[f] || '')).map(f => [f, row[f] || '']));
    if (!Object.keys(changes).length) return;
    setRowStatus(s => ({ ...s, [row._id]: 'saving' }));
    try {
      const r = await updateMeggerRow(test._id, row._id, changes);
      savedRef.current[row._id] = { ...r.data };
      setRowStatus(s => ({ ...s, [row._id]: 'saved' }));
    } catch {
      setRowStatus(s => ({ ...s, [row._id]: 'error' }));
    }
  };

  const rowInput = (row, field, placeholder) => (
    <input
      className="report-input"
      value={row[field] || ''}
      placeholder={placeholder}
      disabled={!canEdit}
      enterKeyHint="next"
      onChange={e => setRowField(row._id, field, e.target.value)}
      onBlur={() => saveRow(test.rows.find(r => r._id === row._id) || row)}
      onKeyDown={focusNextOnEnter}
    />
  );

  const infoLines = test ? [
    `Test date: ${showDate(test.date)}`,
    test.testVoltage && `Test voltage: ${test.testVoltage}`,
    test.measuredBy && `Measured by: ${test.measuredBy}`,
    test.remarks && `Remarks: ${test.remarks}`,
  ].filter(Boolean) : [];

  return (
    <div className="module-wrap">
      <div className="module-toolbar report-toolbar">
        <span className="module-toolbar-title">Megger Lists</span>
        <SwitchboardSelect {...picker} />
        {tests.length > 0 && (
          <select className="report-test-select" value={testId} onChange={e => setTestId(e.target.value)}>
            {tests.map(t => (
              <option key={t._id} value={t._id}>
                {showDate(t.date)} · {filled(t)}/{t.rows.length} measured
              </option>
            ))}
          </select>
        )}
        {canEdit && sb && (
          <button className="btn-primary" onClick={handleNew}><VscAdd size={14} style={{ marginRight: 4 }} />New test</button>
        )}
        {test && PRINT_ROLES.includes(role) && (
          <PrintButton
            title={`Megger List — ${sb.name} — ${project?.name ?? ''}`}
            storageKey="megger"
            rows={test.rows}
            infoLines={infoLines}
            onOpenTab={onOpenTab}
            columns={[
              { key: 'breaker',   label: 'Breaker',                  render: r => r.breaker || '' },
              { key: 'cable',     label: 'Cable',                    render: r => r.cableTag || '' },
              { key: 'cableType', label: 'Cable type',               render: r => r.cableType || '' },
              { key: 'consumer',  label: 'Consumer',                 render: r => r.consumer || '' },
              // The PDF font has no Ω, so the print headers say MOhm
              { key: 'initial',   label: 'Initial [MOhm]',   render: r => r.initial || '',   pdfWidth: 34 },
              { key: 'corrected', label: 'Corrected [MOhm]', render: r => r.corrected || '', pdfWidth: 34 },
              { key: 'remarks',   label: 'Remarks',                  render: r => r.remarks || '',   pdfWidth: 60 },
            ]}
          />
        )}
        {test && DELETE_ROLES.includes(role) && (
          <button className="btn-icon" title="Delete this test" onClick={handleDelete}><VscTrash size={14} /></button>
        )}
      </div>

      <div className="module-body report-body">
        {!sb && <div className="empty-state">Add switchboards and breakers under Reference Tables first.</div>}
        {sb && !test && (
          <div className="empty-state">
            No megger tests for {sb.name} yet.{canEdit && ' Press New test to start one — it lists every cable leaving this switchboard.'}
          </div>
        )}

        {test && header && (
          <>
            <div className="megger-header">
              <label>Test date
                <input type="date" value={header.date} disabled={!canEdit}
                  onChange={e => setHeader(h => ({ ...h, date: e.target.value }))} onBlur={() => saveHeader('date')} />
              </label>
              <label>Test voltage
                <input list="megger-voltages" value={header.testVoltage} placeholder="e.g. 500 V" disabled={!canEdit}
                  onChange={e => setHeader(h => ({ ...h, testVoltage: e.target.value }))} onBlur={() => saveHeader('testVoltage')} />
                <datalist id="megger-voltages">{VOLTAGES.map(v => <option key={v} value={v} />)}</datalist>
              </label>
              <label>Measured by
                <input value={header.measuredBy} disabled={!canEdit}
                  onChange={e => setHeader(h => ({ ...h, measuredBy: e.target.value }))} onBlur={() => saveHeader('measuredBy')} />
              </label>
              <label>Remarks
                <input value={header.remarks} disabled={!canEdit}
                  onChange={e => setHeader(h => ({ ...h, remarks: e.target.value }))} onBlur={() => saveHeader('remarks')} />
              </label>
            </div>

            <div className="megger-subbar">
              <span className="report-count">{filled(test)} of {test.rows.length} cables measured</span>
              {canEdit && (
                <button className="btn-secondary" onClick={handleAddNew} title="Add cables connected to this switchboard after the test was created">
                  <VscRefresh size={13} style={{ marginRight: 4 }} />Refresh
                </button>
              )}
              {notice && <span className="report-notice">{notice}</span>}
            </div>

            <table className="data-table report-table megger-table">
              <thead>
                <tr>
                  <th>Breaker</th><th>Cable</th><th>Cable type</th><th>Consumer</th>
                  <th>Initial [MΩ]</th><th>Corrected [MΩ]</th><th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {test.rows.map(r => (
                  <tr key={r._id} className={rowStatus[r._id] ? `megger-${rowStatus[r._id]}` : ''}>
                    <td className="report-breaker" data-label="Breaker">{r.breaker}</td>
                    <td data-label="Cable">{r.cableTag}</td>
                    <td data-label="Cable type">{r.cableType}</td>
                    <td className="megger-consumer" data-label="Consumer">{r.consumer}</td>
                    <td data-label="Initial [MΩ]">{rowInput(r, 'initial', 'MΩ')}</td>
                    <td data-label="Corrected [MΩ]">{rowInput(r, 'corrected', 'MΩ')}</td>
                    <td data-label="Remarks">{rowInput(r, 'remarks', '')}</td>
                  </tr>
                ))}
                {test.rows.length === 0 && (
                  <tr><td colSpan={7} className="report-muted">No cables leave this switchboard (cables need a From breaker).</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default MeggerLists;
